import { useMutation, useQuery } from '@apollo/client/react'
import {
  Button,
  Checkbox,
  FailureNotice,
  InlineConfirmation,
  Notice,
  Section,
  useTranslate,
  PANEL_SURFACE,
} from '@athena/ui'
import { PLANS, isPlanCode } from '@athena/shared'
import { useState } from 'react'

import {
  MyPracticesDocument,
  MyStudentsDocument,
  SaveDowngradeSelectionDocument,
  type SubscriptionFieldsFragment,
} from '../../graphql/generated/operations.js'
import { useFormat } from '../../lib/format.js'
import { useFirstUserErrorText } from '../../lib/user-errors.js'

import { planName } from './planName.js'

export interface DowngradeSelectionProps {
  subscription: SubscriptionFieldsFragment
}

/**
 * Choosing what survives a scheduled downgrade.
 *
 * Shown only while a change is scheduled and the preview says something would
 * be suspended — a tutor already inside the smaller plan's limits is not asked
 * to choose between things that all fit.
 *
 * ── What the tutor is agreeing to ────────────────────────────────────────
 *
 * The copy is explicit that nothing is deleted. Everything unpicked is *set
 * aside*: its versions, its assignments and every attempt ever made stay
 * exactly where they are, and it comes back when there is room. That is the
 * whole difference between this and a product that deletes what you stop
 * paying for, and it is worth saying in the interface rather than only in the
 * schema.
 *
 * ── Unchecked is not "delete", it is "oldest first" ──────────────────────
 *
 * A tutor who saves nothing still gets a deterministic outcome: the server
 * keeps the oldest created first, ties broken by ascending id. The preview
 * above shows exactly what that would suspend, so the default is visible
 * rather than a surprise on the day.
 */
export function DowngradeSelection({ subscription }: DowngradeSelectionProps) {
  const t = useTranslate()
  const format = useFormat()
  const userErrorText = useFirstUserErrorText()

  /**
   * The lists to choose from, fetched only when there is a choice to make.
   *
   * `skip` rather than an early return above the hooks, because hooks cannot
   * be called conditionally — and without it every tutor's subscription screen
   * would fetch their whole student list and their whole shelf to render
   * nothing, on every visit.
   */
  const choosing = subscription.scheduledPlanCode !== null
  const students = useQuery(MyStudentsDocument, { skip: !choosing })
  const practices = useQuery(MyPracticesDocument, {
    variables: { subjectId: null },
    skip: !choosing,
  })

  const [keepStudents, setKeepStudents] = useState<string[]>(subscription.keepStudentIds)
  const [keepPractices, setKeepPractices] = useState<string[]>(subscription.keepPracticeIds)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  /** A save that never got an answer. The ticks stay as the tutor left them, for the retry. */
  const [failure, setFailure] = useState<unknown>()

  const [save, saving] = useMutation(SaveDowngradeSelectionDocument, {
    refetchQueries: ['MySubscription'],
  })

  /**
   * Save the ticks as they are now. Sending the same selection twice leaves
   * the same selection, so a retry after a request that never came back is
   * safe. Confirmed only when the server says the selection is saved.
   */
  async function onSave() {
    setError(null)
    setSaved(false)
    let errors
    try {
      const result = await save({
        variables: { studentIds: keepStudents, practiceIds: keepPractices },
      })
      errors = result.data?.saveDowngradeSelection.userErrors
    } catch (caught) {
      setFailure(caught)
      return
    }
    setFailure(undefined)
    if (errors === undefined) setFailure(new Error('The selection came back without a result.'))
    else if (errors.length > 0) setError(userErrorText(errors, t('common.tryAgain')))
    else setSaved(true)
  }

  const target = subscription.scheduledPlanCode
  if (!target || !isPlanCode(target)) return null

  const plan = PLANS[target]
  const preview = subscription.downgradePreview
  if (!preview || (preview.studentIds.length === 0 && preview.practiceIds.length === 0)) {
    return null
  }

  const overStudents = keepStudents.length > plan.maxStudents
  const overPractices =
    plan.maxActivePractices !== null && keepPractices.length > plan.maxActivePractices

  return (
    <Section
      id="downgrade-selection"
      title={t('subscription.selectionHeading')}
      className={PANEL_SURFACE}
    >
      <p className="text-body text-text-secondary">
        {t('subscription.selectionBody', [
          format.date(subscription.scheduledEffectiveAt ?? ''),
          plan.maxStudents,
          plan.maxActivePractices ?? t('subscription.unlimited'),
        ])}
      </p>

      <fieldset className="flex flex-col gap-2">
        <legend className="text-label font-semibold uppercase text-text-muted">
          {t('subscription.selectionStudents')} ·{' '}
          {t('subscription.ofLimit', [keepStudents.length, plan.maxStudents])}
        </legend>
        {(students.data?.myStudents ?? [])
          .filter((student) => !student.suspended)
          .map((student) => (
            <Checkbox
              key={student.id}
              checked={keepStudents.includes(student.id)}
              onChange={() => setKeepStudents(toggle(keepStudents, student.id))}
            >
              {student.displayName}
            </Checkbox>
          ))}
        {overStudents ? (
          <p className="text-body-sm text-error-text">
            {t('subscription.selectionTooMany', [plan.maxStudents])}
          </p>
        ) : null}
      </fieldset>

      <fieldset className="flex flex-col gap-2">
        <legend className="text-label font-semibold uppercase text-text-muted">
          {t('subscription.selectionPractices')} ·{' '}
          {plan.maxActivePractices === null
            ? t('subscription.unlimitedUsage', [keepPractices.length])
            : t('subscription.ofLimit', [keepPractices.length, plan.maxActivePractices])}
        </legend>
        {/* The active half only. An archived practice is already out of the
            active set, so it is not competing for a slot and asking a tutor to
            choose about it would be asking a question with no consequence. */}
        {(practices.data?.active ?? [])
          .filter((practice) => !practice.suspended)
          .map((practice) => (
            <Checkbox
              key={practice.id}
              checked={keepPractices.includes(practice.id)}
              onChange={() => setKeepPractices(toggle(keepPractices, practice.id))}
            >
              {practice.title}
            </Checkbox>
          ))}
        {overPractices && plan.maxActivePractices !== null ? (
          <p className="text-body-sm text-error-text">
            {t('subscription.selectionTooMany', [plan.maxActivePractices])}
          </p>
        ) : null}
      </fieldset>

      {error ? <Notice tone="error" title={error} /> : null}
      {failure === undefined ? null : (
        <FailureNotice
          title={t('subscription.selectionNotSaved')}
          error={failure}
          retrying={saving.loading}
          onRetry={() => void onSave()}
        />
      )}

      <div className="flex flex-wrap items-center gap-3">
        <Button
          loading={saving.loading}
          loadingText={t('common.saving')}
          // Disabled for a selection the server would refuse, and the server
          // refuses it too: this is the UX half of the rule, never the rule.
          disabled={overStudents || overPractices}
          onClick={() => void onSave()}
        >
          {t('subscription.selectionSave')}
        </Button>
        {saved ? <InlineConfirmation>{t('subscription.selectionSaved')}</InlineConfirmation> : null}
      </div>

      <p className="text-body-sm text-text-secondary">
        {t('subscription.scheduledBody', [
          planName(t, target),
          format.date(subscription.scheduledEffectiveAt ?? ''),
        ])}
      </p>
    </Section>
  )
}

function toggle(ids: readonly string[], id: string): string[] {
  return ids.includes(id) ? ids.filter((value) => value !== id) : [...ids, id]
}
